import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  FileText, CheckCircle2, ShieldCheck, Download, RotateCcw, 
  Calendar, User, PenTool, Lock, AlertCircle, FileSignature, 
  RefreshCw, Upload, MapPin, Camera
} from 'lucide-react';
import { documentService } from '../services/documentService';
import { apiService } from '../services/api';

const API_URL = apiService.baseURL;

export default function SignatureView({ token }) {
  const [currentDoc, setCurrentDoc] = useState(null);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [signed, setSigned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [error, setError] = useState('');
  const [signatureCount, setSignatureCount] = useState(0);
  
  // 👇 NUEVO: Estado para controlar la descarga del documento firmado
  const [downloadingSigned, setDownloadingSigned] = useState(false);
  
  // Estados de ubicación mejorados
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [locationStatus, setLocationStatus] = useState('prompt'); // 'prompt', 'loading', 'success', 'denied', 'gps_off', 'timeout', 'error'
  const [locationPermission, setLocationPermission] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const [locationError, setLocationError] = useState('');
  
  // Estados para la cámara y permisos
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [cameraPermission, setCameraPermission] = useState('prompt');

  const MARGIN_PERCENT = 12;
  const [signaturePosition, setSignaturePosition] = useState({ x: 70, y: 80 });
  const [signatureScale, setSignatureScale] = useState(1);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

// 👇 FUNCIÓN MEJORADA CON DETECCIÓN DE GPS DESACTIVADO
const requestLocationPermission = useCallback(async () => {
  if (!("geolocation" in navigator)) {
    setLocationStatus('unsupported');
    setLocationPermission('denied');
    setLocationError('Tu navegador no soporta geolocalización.');
    return;
  }

  try {
    setLocationStatus('loading');
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // ✅ ÉXITO: Ubicación obtenida
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setLocationStatus('success');
        setLocationPermission('granted');
      },
      (error) => {
        console.error("Error de geolocalización:", error);
        
        if (error.code === error.PERMISSION_DENIED) {
          // ❌ Permiso denegado por el usuario
          setLocationStatus('denied');
          setLocationPermission('denied');
          setLocationError('Permiso denegado. Haz clic en el ícono  en la barra de direcciones y permite el acceso a la ubicación.');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          // ❌ GPS desactivado o sin señal (común en móviles)
          setLocationStatus('gps_off');
          setLocationPermission('prompt');
          setLocationError('El GPS está desactivado en tu dispositivo. Actívalo desde la configuración de tu teléfono.');
        } else if (error.code === error.TIMEOUT) {
          // ⏱️ Tiempo agotado (GPS lento o sin señal)
          setLocationStatus('timeout');
          setLocationPermission('prompt');
          setLocationError('No se pudo obtener la ubicación. Verifica que el GPS esté activado y tengas buena señal.');
        } else {
          // ❓ Error desconocido
          setLocationStatus('error');
          setLocationPermission('denied');
          setLocationError('Error al obtener ubicación: ' + error.message);
        }
      },
      {
        enableHighAccuracy: true,  // Forzar uso de GPS
        timeout: 15000,            // 15 segundos (más tiempo para móviles)
        maximumAge: 0
      }
    );
  } catch (err) {
    console.error("Error al solicitar ubicación:", err);
    setLocationStatus('error');
    setLocationPermission('denied');
    setLocationError('Error al acceder a la ubicación.');
  }
}, []);

  // Función para reintentar después de cambiar permisos
  const resetLocationPermission = () => {
    setLocationError('');
    setLocationPermission('prompt');
    setLocationStatus('prompt');
    setTimeout(() => requestLocationPermission(), 100);
  };

  // Ejecutar automáticamente al montar el componente
  useEffect(() => {
    if (token) loadDocument(token);
    requestLocationPermission(); // 👈 Solicitar ubicación al iniciar (similar a cámara)
  }, [token, requestLocationPermission]);

  useEffect(() => {
    if (showCamera && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [showCamera]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1e40af';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const loadDocument = async (docToken) => {
    setLoadingDoc(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/documents/${docToken}`);
      const data = await res.json();
      if (data.success) {
        setCurrentDoc(data.document);
      } else {
        setError(data.error || 'No se pudo cargar el documento');
      }
    } catch (err) {
      setError('Error al conectar con el servidor');
    } finally {
      setLoadingDoc(false);
    }
  };

  const openCamera = async () => {
    try {
      setCameraError('');
      if (navigator.permissions) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'camera' });
          setCameraPermission(permissionStatus.state);
          if (permissionStatus.state === 'denied') {
            setCameraError('Permiso bloqueado. Habilítalo en la barra de direcciones y haz clic en "Reintentar".');
            return;
          }
          permissionStatus.onchange = () => {
            setCameraPermission(permissionStatus.state);
            if (permissionStatus.state === 'granted') setCameraError('');
          };
        } catch (e) {
          console.warn('Permissions API no soportada');
        }
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      setShowCamera(true);
      setCameraPermission('granted');
    } catch (err) {
      console.error("Error al acceder a la cámara:", err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraError('Permiso denegado. Haz clic en el ícono 🔒 en la barra de direcciones, permite la cámara y luego pulsa "Reintentar".');
        setCameraPermission('denied');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No se encontró una cámara en este dispositivo.');
      } else if (err.name === 'NotReadableError') {
        setCameraError('La cámara está siendo usada por otra aplicación.');
      } else {
        setCameraError('Error al acceder a la cámara: ' + err.message);
      }
    }
  };

  const resetCameraPermission = () => {
    setCameraError('');
    setCameraPermission('prompt');
    setTimeout(() => openCamera(), 100);
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const photoData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(photoData);
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      setShowCamera(false);
    }
  };

  const cancelPhoto = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCapturedPhoto(null);
    setCameraError('');
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    setIsDrawing(true);
    ctx.beginPath();
    const x = (e.clientX ? e.clientX - rect.left : e.touches[0].clientX - rect.left) * scaleX;
    const y = (e.clientY ? e.clientY - rect.top : e.touches[0].clientY - rect.top) * scaleY;
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (e.clientX ? e.clientX - rect.left : e.touches[0].clientX - rect.left) * scaleX;
    const y = (e.clientY ? e.clientY - rect.top : e.touches[0].clientY - rect.top) * scaleY;

    ctx.lineTo(x, y);
    ctx.stroke();
    setSignatureCount(prev => prev + 1);
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1e40af';
    setSignatureCount(0);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('La imagen no debe superar los 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        setSignatureMode('upload');
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const containerRect = container.getBoundingClientRect();

    const handlePointerMove = (moveEvent) => {
      const moveX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const moveY = moveEvent.touches ? moveEvent.touches[0].clientY : moveEvent.clientY;
      
      const centerX = moveX - containerRect.left;
      const centerY = moveY - containerRect.top;
      
      let percentX = (centerX / containerRect.width) * 100;
      let percentY = (centerY / containerRect.height) * 100;
      
      percentX = Math.max(MARGIN_PERCENT, Math.min(100 - MARGIN_PERCENT, percentX));
      percentY = Math.max(MARGIN_PERCENT, Math.min(100 - MARGIN_PERCENT, percentY));
      
      setSignaturePosition({ x: percentX, y: percentY });
    };

    const handlePointerUp = () => {
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchmove', handlePointerMove, { passive: false });
      document.removeEventListener('touchend', handlePointerUp);
    };

    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', handlePointerUp);
    document.addEventListener('touchmove', handlePointerMove, { passive: false });
    document.addEventListener('touchend', handlePointerUp);
  };

  const downloadOriginalDocument = async () => {
    try {
      await documentService.downloadOriginal(token, currentDoc?.originalName);
    } catch (error) {
      alert('Error al descargar el documento original: ' + error.message);
    }
  };

  // 👇 FUNCIÓN DE DESCARGA CON ESTADO DE CARGA
  const downloadSignedDocument = async () => {
    setDownloadingSigned(true);
    try {
      await documentService.downloadSigned(token, currentDoc?.originalName);
    } catch (error) {
      alert('Error al descargar el documento firmado: ' + error.message);
    } finally {
      setDownloadingSigned(false);
    }
  };

  const handleSign = async () => {
    if (!accepted) {
      alert('Debes aceptar los términos para continuar');
      return;
    }

    if (signatureMode === 'draw' && signatureCount === 0) {
      alert('Por favor, dibuja tu firma antes de continuar');
      return;
    }
    if (signatureMode === 'upload' && !uploadedImage) {
      alert('Por favor, sube una imagen de tu firma');
      return;
    }

    // Validación estricta: requerir ubicación
    if (locationStatus !== 'success') {
      alert('Es necesario activar la ubicación para firmar este documento. Esto es requerido para la validez legal de la firma. Por favor, activa la ubicación y reintenta.');
      return;
    }

    const signatureImage = signatureMode === 'upload' 
      ? uploadedImage 
      : canvasRef.current.toDataURL('image/png');

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/documents/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signatureImage,
          acceptedAt: new Date().toISOString(),
          signatureScale,
          signaturePosition,
          latitude: location.lat,
          longitude: location.lng,
          signerPhoto: capturedPhoto
        })
      });

      const data = await res.json();
      if (data.success) {
        setSigned(true);
      } else {
        alert('Error al firmar el documento: ' + (data.error || 'Error desconocido'));
      }
    } catch (error) {
      alert('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  if (loadingDoc) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="relative mb-6 mx-auto w-20 h-20">
            <div className="absolute inset-0 border-4 border-slate-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
            <FileSignature className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">Cargando documento</h3>
          <p className="text-sm text-slate-500">Preparando el entorno de firma segura...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-lg w-full text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">No se pudo cargar el documento</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <button onClick={() => window.location.reload()} className="w-full bg-slate-900 text-white px-4 py-2.5 rounded-lg hover:bg-slate-800 transition-colors font-medium text-sm flex items-center justify-center gap-2">
            <RefreshCw className="w-4 h-4" /> Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-2xl w-full text-center">
          <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Documento Firmado Exitosamente</h1>
          <p className="text-slate-600 mb-8">Tu firma, foto de identificación y ubicación han sido registradas y almacenadas de forma segura.</p>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 mb-8 text-left">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Documento</p>
                <p className="text-sm font-semibold text-slate-900 truncate">{currentDoc?.originalName}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Firmante</p>
                <p className="text-sm font-semibold text-slate-900">{currentDoc?.clientName}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Fecha y Hora</p>
                <p className="text-sm font-semibold text-slate-900">{new Date().toLocaleString('es-ES')}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {/* 👇 BOTÓN CON ESTADO DE CARGA Y DESHABILITADO */}
            <button 
              onClick={downloadSignedDocument} 
              disabled={downloadingSigned}
              className="flex items-center justify-center gap-2 bg-slate-900 text-white px-6 py-2.5 rounded-lg hover:bg-slate-800 transition-colors font-medium text-sm shadow-sm disabled:bg-slate-600 disabled:cursor-not-allowed"
            >
              {downloadingSigned ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                  Descargando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" /> Descargar Copia Firmada
                </>
              )}
            </button>
            
            <button onClick={() => window.close()} className="flex items-center justify-center gap-2 bg-white border border-slate-300 text-slate-700 px-6 py-2.5 rounded-lg hover:bg-slate-50 transition-colors font-medium text-sm">
              Cerrar Ventana
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-900 rounded-md flex items-center justify-center">
              <FileSignature className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-slate-900 tracking-tight">Firma</span>
          </div>
          <div className="flex items-center gap-2 text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100">
            <Lock className="w-3 h-3" />
            <span>Sesión de Firma Segura (TLS 1.3)</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-1">Documento a Firmar</h3>
                  <p className="text-sm font-semibold text-slate-900 break-words">{currentDoc?.originalName}</p>
                </div>
              </div>
              <div className="space-y-3 pt-4 border-t border-slate-100">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500 flex items-center gap-2"><User className="w-4 h-4" /> Firmante</span>
                  <span className="text-sm font-medium text-slate-900">{currentDoc?.clientName}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500 flex items-center gap-2"><Calendar className="w-4 h-4" /> Estado</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">Pendiente de firma</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 rounded-xl p-5 text-white shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-5 h-5 text-blue-400" />
                <h3 className="font-semibold text-sm">Garantía de Seguridad</h3>
              </div>
              <ul className="space-y-4 text-xs text-slate-300">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></div>
                  Certificado digital auditable
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></div>
                  Encriptación de extremo a extremo (AES-256)
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></div>
                  Sello de tiempo certificado (RFC 3161)
                </li>
                
                {/* 👇 ESTADO DE UBICACIÓN CON UI MEJORADA */}
                <li className="flex flex-col gap-2 pt-1">
                  <div className="flex items-start gap-2">
                    <MapPin className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
                      locationStatus === 'success' ? 'text-emerald-400' : 
                      locationStatus === 'loading' ? 'text-blue-400 animate-pulse' : 
                      locationStatus === 'gps_off' ? 'text-red-400' :
                      locationStatus === 'timeout' ? 'text-amber-400' :
                      'text-amber-400'
                    }`} />
                    <span className="text-slate-300">
                      Geolocalización: 
                      {locationStatus === 'loading' && <span className="text-blue-300 ml-1">Solicitando permiso...</span>}
                      {locationStatus === 'success' && <span className="text-emerald-300 ml-1 font-medium">Activada</span>}
                      {locationStatus === 'gps_off' && <span className="text-red-300 ml-1 font-medium">GPS desactivado</span>}
                      {locationStatus === 'timeout' && <span className="text-amber-300 ml-1 font-medium">Tiempo agotado</span>}
                      {locationStatus === 'denied' && <span className="text-amber-300 ml-1 font-medium">Requiere activación</span>}
                      {locationStatus === 'prompt' && <span className="text-blue-300 ml-1">Verificando...</span>}
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Acciones</h3>
              <button onClick={downloadOriginalDocument} className="w-full flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-colors group">
                <div className="flex items-center gap-3">
                  <Download className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
                  <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">Descargar original</span>
                </div>
              </button>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-slate-50 border-b border-slate-200 px-6 py-5">
                <h2 className="text-lg font-semibold text-slate-900">Proceso de Firma Digital</h2>
                <p className="text-sm text-slate-500 mt-1">Complete los siguientes pasos para validar el documento.</p>
              </div>

              <div className="p-6 space-y-8">
                {/* 👇 SECCIÓN DE UBICACIÓN - CON MANEJO DE GPS DESACTIVADO */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-7 h-7 bg-slate-900 text-white rounded-full flex items-center justify-center text-xs font-bold">0</div>
                    <h3 className="text-base font-semibold text-slate-900">Activar ubicación</h3>
                  </div>
                  
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    {locationStatus === 'success' ? (
                      // ✅ UBICACIÓN ACTIVADA
                      <div className="text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-full mb-3">
                          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                        </div>
                        <p className="text-sm font-semibold text-emerald-700 mb-1">Ubicación activada correctamente</p>
                        <p className="text-xs text-slate-500">Coordenadas registradas para validez legal</p>
                      </div>
                    ) : locationStatus === 'loading' ? (
                      //  CARGANDO
                      <div className="text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
                          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
                        </div>
                        <p className="text-sm font-semibold text-blue-700 mb-1">Solicitando permiso de ubicación...</p>
                        <p className="text-xs text-slate-500">Por favor, acepta el permiso en tu navegador</p>
                      </div>
                    ) : locationStatus === 'gps_off' ? (
                      // 📱 GPS DESACTIVADO (ESPECÍFICO PARA MÓVILES)
                      <div className="text-center">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left mb-4">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-semibold text-red-900 mb-2">GPS desactivado</p>
                              <p className="text-xs text-red-800 mb-3">
                                El GPS de tu dispositivo está apagado. Para firmar este documento, necesitas activarlo.
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                          <p className="text-xs font-semibold text-blue-900 mb-2">📍 ¿Cómo activar el GPS?</p>
                          <ol className="text-xs text-blue-800 space-y-2 text-left">
                            <li className="flex items-start gap-2">
                              <span className="font-bold">Android:</span>
                              <span>Desliza hacia abajo desde la parte superior y toca "Ubicación" o "GPS"</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="font-bold">iPhone:</span>
                              <span>Ve a Configuración &gt; Privacidad &gt; Servicios de Ubicación y actívalo</span>
                            </li>
                          </ol>
                        </div>
                        
                        <button
                          onClick={resetLocationPermission}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm w-full justify-center"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Reintentar después de activar GPS
                        </button>
                      </div>
                    ) : locationStatus === 'timeout' ? (
                      // ⏱️ TIEMPO AGOTADO
                      <div className="text-center">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left mb-4">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-semibold text-amber-900 mb-2">Tiempo de espera agotado</p>
                              <p className="text-xs text-amber-800 mb-3">
                                No se pudo obtener tu ubicación. Esto puede deberse a:
                              </p>
                              <ul className="text-xs text-amber-800 space-y-1 list-disc list-inside">
                                <li>GPS desactivado</li>
                                <li>Mala señal de GPS</li>
                                <li>Estás en un lugar cerrado</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                        
                        <button
                          onClick={resetLocationPermission}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm w-full justify-center"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Reintentar
                        </button>
                      </div>
                    ) : (
                      // ❌ PERMISO DENEGADO O NO SOLICITADO
                      <div className="text-center">
                        {locationPermission === 'denied' ? (
                          <div className="space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                              <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-semibold text-amber-900 mb-2">Permiso de ubicación requerido</p>
                                  <p className="text-xs text-amber-800 mb-3">
                                    {locationError || 'Para firmar este documento, es necesario activar la ubicación.'}
                                  </p>
                                  <div className="bg-white/50 rounded p-3 mt-2">
                                    <p className="text-xs font-semibold text-amber-900 mb-1"> ¿Cómo activarlo?</p>
                                    <ol className="text-xs text-amber-800 space-y-1 list-decimal list-inside">
                                      <li>Haz clic en el ícono 🔒 en la barra de direcciones</li>
                                      <li>Selecciona "Permitir" en Ubicación</li>
                                      <li>Presiona "Activar Ubicación" de abajo</li>
                                    </ol>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={resetLocationPermission}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm w-full justify-center"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Activar Ubicación
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="mb-4">
                              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
                                <MapPin className="w-6 h-6 text-blue-600" />
                              </div>
                              <p className="text-sm font-semibold text-slate-900 mb-1">Se requiere activar la ubicación</p>
                              <p className="text-xs text-slate-600">
                                La geolocalización es necesaria para la validez legal del documento firmado.
                              </p>
                            </div>
                            <button
                              onClick={requestLocationPermission}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm w-full justify-center"
                            >
                              <MapPin className="w-4 h-4" />
                              Activar Ubicación
                            </button>
                            {locationError && (
                              <p className="text-xs text-red-600 mt-3 whitespace-pre-line">{locationError}</p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Sección de foto del firmante */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      capturedPhoto ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-white'
                    }`}>
                      1
                    </div>
                    <h3 className="text-base font-semibold text-slate-900">Foto de identificación</h3>
                  </div>
                  
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    {!capturedPhoto ? (
                      <div className="text-center">
                        {cameraPermission === 'denied' ? (
                          <div className="space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                              <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-semibold text-amber-900 mb-2">Permiso de cámara bloqueado</p>
                                  <p className="text-xs text-amber-800 mb-3 whitespace-pre-line">{cameraError}</p>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={resetCameraPermission}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Reintentar con permisos habilitados
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={openCamera}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                            >
                              <Camera className="w-4 h-4" />
                              Abrir cámara y tomar foto
                            </button>
                            {cameraError && (
                              <p className="text-xs text-red-600 mt-3 whitespace-pre-line">{cameraError}</p>
                            )}
                            <p className="text-xs text-slate-500 mt-3">
                              Se requiere una foto para verificar tu identidad
                            </p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="text-center">
                        <img 
                          src={capturedPhoto} 
                          alt="Foto del firmante" 
                          className="max-w-xs mx-auto rounded-lg border border-slate-300 mb-3"
                        />
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={cancelPhoto}
                            className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors"
                          >
                            Volver a tomar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      (signatureMode === 'draw' && signatureCount > 0) || (signatureMode === 'upload' && uploadedImage)
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-slate-900 text-white'
                    }`}>
                      2
                    </div>
                    <h3 className="text-base font-semibold text-slate-900">Proporcione su firma</h3>
                  </div>

                  <div className="flex gap-2 mb-4 border-b border-slate-200">
                    <button 
                      onClick={() => setSignatureMode('draw')}
                      className={`pb-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${signatureMode === 'draw' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                      <PenTool className="w-4 h-4" /> Dibujar
                    </button>
                    <button 
                      onClick={() => setSignatureMode('upload')}
                      className={`pb-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${signatureMode === 'upload' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                      <Upload className="w-4 h-4" /> Subir Imagen
                    </button>
                  </div>

                  {signatureMode === 'draw' ? (
                    <div className="border border-slate-300 rounded-lg overflow-hidden bg-white relative">
                      <canvas
                        ref={canvasRef}
                        width={800}
                        height={200}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        className="w-full h-48 cursor-crosshair touch-none block"
                        style={{ backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '20px 20px' }}
                      />
                      {signatureCount === 0 && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="text-slate-300 text-sm font-medium tracking-wide uppercase">Firme dentro de este recuadro</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-500 hover:bg-blue-50/50 transition-all relative">
                      <input 
                        type="file" 
                        accept="image/png, image/jpeg, image/jpg" 
                        onChange={handleImageUpload} 
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      />
                      {uploadedImage ? (
                        <div className="relative">
                          <img src={uploadedImage} alt="Firma" className="max-h-32 mx-auto" />
                          <p className="text-xs text-slate-500 mt-2">Haz clic para cambiar la imagen</p>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm font-medium text-slate-700">Haz clic para subir tu firma</p>
                          <p className="text-xs text-slate-500 mt-1">Formatos: PNG, JPG (Máx. 2MB)</p>
                        </>
                      )}
                    </div>
                  )}

                  {signatureMode === 'draw' && (
                    <div className="flex justify-between items-center mt-3">
                      <button onClick={clearSignature} className="flex items-center gap-2 text-sm text-slate-600 hover:text-red-600 transition-colors font-medium px-3 py-1.5 rounded-md hover:bg-red-50">
                        <RotateCcw className="w-4 h-4" /> Limpiar y reiniciar
                      </button>
                      {signatureCount > 0 && (
                        <span className="text-sm text-emerald-700 font-medium flex items-center gap-2 bg-emerald-50 px-3 py-1.5 rounded-md border border-emerald-100">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div> Firma registrada
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-7 h-7 bg-slate-900 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                    <h3 className="text-base font-semibold text-slate-900">Aceptación de términos legales</h3>
                  </div>

                  <div className={`p-5 rounded-lg border transition-all duration-200 ${accepted ? 'bg-emerald-50/50 border-emerald-200' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}>
                    <label className="flex items-start gap-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                        className="w-5 h-5 mt-0.5 flex-shrink-0 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-600/20 focus:ring-offset-0 transition-colors"
                      />
                      <div>
                        <p className="font-semibold text-slate-900 mb-1">Confirmo mi aceptación y conformidad</p>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          He leído y comprendo completamente el contenido de este documento. Reconozco que mi firma digital tiene plena validez legal equivalente a una firma manuscrita, y acepto los términos y condiciones establecidos en el mismo.
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="pt-6 border-t border-slate-100 mt-6">
                    <button
                      onClick={handleSign}
                      disabled={!accepted || loading || (signatureMode === 'draw' && signatureCount === 0) || (signatureMode === 'upload' && !uploadedImage) || !capturedPhoto || locationStatus !== 'success'}
                      className="w-full bg-slate-900 text-white py-3.5 rounded-lg hover:bg-slate-800 active:bg-slate-950 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed font-semibold text-sm transition-all duration-200 shadow-sm flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                          Procesando firma digital y sello de tiempo...
                        </>
                      ) : (
                        <>
                          <ShieldCheck className="w-4 h-4" />
                          Confirmar y Firmar Documento
                        </>
                      )}
                    </button>
                    <p className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center gap-1.5">
                      <Lock className="w-3 h-3" />
                      Transacción protegida y auditada. Su información no será compartida con terceros.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal de cámara */}
      {showCamera && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 text-center">Toma tu foto</h3>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full rounded-lg bg-black aspect-video"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={cancelPhoto}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={capturePhoto}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Capturar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}